import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppContext } from '../context/AppContext';
import { v4 as uuidv4 } from 'uuid';
import './IntakeForm.css';

const IntakeForm = () => {
  const { updateCase } = useAppContext();
  const navigate = useNavigate();

  const [dispositionSelection, setDispositionSelection] = useState('Open');
  const [customDisposition, setCustomDisposition] = useState('');

  // Removed the "amountDue" field from the initial state since it is calculated.
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    dob: '',
    phone: '',
    secondaryPhone: '',
    tertiaryPhone: '',
    email: '',
    mailingAddress: '',
    incidentCounty: '',
    caseType: '',
    officerName: '',
    appearanceNumber: '',
    idnumber: '',
    courtDateTime: '',
    violationDate: '',
    totalFee: '',
    paidAmount: '',
    comments: '',
  });

  // Calculate the difference between totalFee and paidAmount.
  const calculateAmountDue = () => {
    const total = parseFloat(formData.totalFee) || 0;
    const paid = parseFloat(formData.paidAmount) || 0;
    return (total - paid).toFixed(2);
  };

  // Determine balance status.
  const getBalanceStatus = () => {
    return calculateAmountDue() === '0.00' ? 'PAID IN FULL' : 'NOT PAID';
  };

  // Handle changes for text or number fields.
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle disposition selection changes.
  const handleDispositionChange = (e) => {
    const value = e.target.value;
    setDispositionSelection(value);
    if (value !== 'Other') setCustomDisposition('');
  };

  // On submit, create a new case object with values transformed as needed.
  const handleSubmit = async (e) => {
    e.preventDefault();

    const finalStatus =
      dispositionSelection === 'Other' ? customDisposition.trim() : dispositionSelection;

    const newCase = {
      id: uuidv4(),
      ...formData,
      status: finalStatus,
      totalFee: parseFloat(formData.totalFee) || 0,
      paidAmount: parseFloat(formData.paidAmount) || 0,
      amountDue: parseFloat(calculateAmountDue()) || 0,
      balanceDue: getBalanceStatus(),
      // courtDateTime stays as the raw datetime-local value; it is interpreted
      // as New York time and converted to UTC centrally in updateCase().
      courtDateTime: formData.courtDateTime || null,
      violationDate: formData.violationDate
        ? new Date(formData.violationDate).toISOString()
        : null,
    };

    try {
      console.log('📦 Submitting case to Amplify:', newCase);
      await updateCase(newCase);
      navigate(`/case/${newCase.id}`);
    } catch (err) {
      console.error('❌ Submission failed:', err);
      alert('Failed to submit case. Please try again.');
    }
  };

  return (
    <div className="container">
      <div className="intake-page-header">
        <h2>New Case Intake Form</h2>
        <p>Enter client and case information to create a new case record</p>
      </div>

      <form onSubmit={handleSubmit} className="intake-form">
        {/* Client Information Section */}
        <div className="form-section">
          <h3 className="form-section-title">Client Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>First Name</label>
              <input name="firstName" value={formData.firstName} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Last Name</label>
              <input name="lastName" value={formData.lastName} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Date of Birth</label>
              <input type="date" name="dob" value={formData.dob} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Phone Number</label>
              <input name="phone" value={formData.phone} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Secondary Phone</label>
              <input name="secondaryPhone" value={formData.secondaryPhone} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" name="email" value={formData.email} onChange={handleChange} />
            </div>
            <div className="form-group full-width">
              <label>Mailing Address</label>
              <input name="mailingAddress" value={formData.mailingAddress} onChange={handleChange} />
            </div>
          </div>
        </div>

        {/* Case Information Section */}
        <div className="form-section">
          <h3 className="form-section-title">Case Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Ticket #</label>
              <input name="appearanceNumber" value={formData.appearanceNumber} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>ID Number</label>
              <input name="idnumber" value={formData.idnumber} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Officer Name</label>
              <input name="officerName" value={formData.officerName} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>County</label>
              <input name="incidentCounty" value={formData.incidentCounty} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Violation Type</label>
              <input name="caseType" value={formData.caseType} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Violation Date</label>
              <input type="date" name="violationDate" value={formData.violationDate} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Court Appearance Date</label>
              <input
                type="datetime-local"
                name="courtDateTime"
                value={formData.courtDateTime}
                onChange={handleChange}
              />
            </div>
            <div className="form-group">
              <label>Disposition</label>
              <select name="status" value={dispositionSelection} onChange={handleDispositionChange}>
                <option value="">Select Disposition</option>
                <option value="NEW">NEW</option>
                <option value="Open">Open</option>
                <option value="Waiting on Date">Waiting on Date</option>
                <option value="MISSED">MISSED</option>
                <option value="GUILTY">GUILTY</option>
                <option value="NOT GUILTY">NOT GUILTY</option>
                <option value="REDUCED">REDUCED</option>
                <option value="UNHIRED">UNHIRED</option>
                <option value="PLED">PLED</option>
                <option value="Closed">Closed</option>
                <option value="Other">Other</option>
              </select>
              {dispositionSelection === 'Other' && (
                <input
                  type="text"
                  placeholder="Enter custom disposition"
                  value={customDisposition}
                  onChange={(e) => setCustomDisposition(e.target.value)}
                  className="custom-disposition"
                />
              )}
            </div>
          </div>
        </div>

        {/* Financial Information Section */}
        <div className="form-section">
          <h3 className="form-section-title">Financial Information</h3>
          <div className="form-grid">
            <div className="form-group">
              <label>Total Fee</label>
              <input type="number" name="totalFee" value={formData.totalFee} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Paid Amount</label>
              <input type="number" name="paidAmount" value={formData.paidAmount} onChange={handleChange} />
            </div>
            <div className="form-group">
              <label>Amount Due</label>
              <input 
                type="text" 
                value={`$${calculateAmountDue()}`} 
                readOnly 
                disabled 
                className="calculated-field"
              />
            </div>
            <div className="form-group">
              <label>Balance Status</label>
              <input 
                type="text" 
                value={getBalanceStatus()} 
                readOnly 
                disabled 
                className={`calculated-field ${getBalanceStatus() === 'PAID IN FULL' ? 'balance-paid' : 'balance-due'}`}
              />
            </div>
          </div>
        </div>

        {/* Additional Notes Section */}
        <div className="form-section">
          <h3 className="form-section-title">Additional Notes</h3>
          <div className="form-grid">
            <div className="form-group full-width">
              <label>Comments</label>
              <textarea 
                name="comments" 
                value={formData.comments} 
                onChange={handleChange}
                placeholder="Enter any additional notes or comments about this case..."
              />
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-button">Submit Case</button>
        </div>
      </form>

      <footer className="settings-footer">
        © {new Date().getFullYear()} Zbrella Inc. All rights reserved.
      </footer>
    </div>
  );
};

export default IntakeForm;
